import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShareNetwork, Copy, Eye, EyeSlash, WhatsappLogo, TwitterLogo } from "@phosphor-icons/react";

/** Card para que el propio atleta (u admin) active el link público de su Trading Card. */
export default function CardShareSection({ userId, athleteName }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [shared, setShared] = useState(false);
  const [token, setToken] = useState(null);

  const publicUrl = token ? `${window.location.origin}/athlete/${token}` : "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}/card/status`);
      setShared(Boolean(data.shared));
      setToken(data.card_token || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${userId}/card/share`);
      setShared(true);
      setToken(data.card_token);
      toast.success("Card pública activada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!window.confirm("¿Desactivar tu card pública? El link dejará de funcionar.")) return;
    setBusy(true);
    try {
      await api.delete(`/users/${userId}/card/share`);
      setShared(false);
      toast.success("Card desactivada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  const shareText = `Mirá mi Athlete Card en Athlon Sports — ${athleteName || ""}`.trim();

  if (loading) {
    return (
      <div className="card-base p-6">
        <div className="text-xs text-muted">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="card-base p-6" data-testid="card-share-section">
      <div className="flex items-center gap-2 mb-4">
        <ShareNetwork size={20} weight="duotone" className="text-primary-app" />
        <h2 className="font-display font-black uppercase text-xl tracking-tighter">Athlete Card Pública</h2>
      </div>

      <p className="text-sm text-muted mb-5 leading-relaxed">
        Un perfil público de una sola página con tus <strong className="text-white">mejores marcas</strong>,
        evolución temporal y badges de logros. Compartilo en WhatsApp, Instagram o Twitter para mostrar tu progreso
        — o para que un club vea de qué sos capaz.
      </p>

      {!shared ? (
        <button
          onClick={activate}
          disabled={busy}
          className="btn-primary flex items-center gap-2 disabled:opacity-40"
          data-testid="activate-card"
        >
          <Eye size={16} weight="bold" /> {busy ? "Activando…" : "Activar card pública"}
        </button>
      ) : (
        <>
          <div className="bg-[#0A0A0A] border border-app p-3 flex items-center gap-3 flex-wrap mb-4" data-testid="card-url-box">
            <div className="flex-1 min-w-0 text-xs font-mono text-white break-all">{publicUrl}</div>
            <button onClick={copyLink} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1" data-testid="copy-card-link">
              <Copy size={12} weight="bold" /> Copiar
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${publicUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1 hover:border-[#25D366] hover:text-[#25D366]"
              data-testid="share-wa"
            >
              <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(publicUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1 hover:border-[#1DA1F2] hover:text-[#1DA1F2]"
              data-testid="share-tw"
            >
              <TwitterLogo size={14} weight="fill" /> Twitter
            </a>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1"
              data-testid="preview-card"
            >
              <Eye size={14} weight="bold" /> Ver card
            </a>
            <button
              onClick={disable}
              disabled={busy}
              className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1 hover:border-[#FF3B30] hover:text-[#FF3B30] disabled:opacity-40"
              data-testid="disable-card"
            >
              <EyeSlash size={14} weight="bold" /> Desactivar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
